export type SimClocks = {
  break: number;
  drive: number;
  shift: number;
  cycle: number;
};

export type RouteBuddyDriverStatus = {
  eldDriverId: number;
  firstName: string;
  lastName: string;
  eldVehicleId: number;
  eldVehicleUnitNumber: string;
  dutyStatus: string;
  clocks: SimClocks;
  currentOffStreak: number;
  lat: number | null;
  lng: number | null;
  locationSource: 'device' | 'eventLine' | 'none';
  locationAt: string | null;
};

export type SimLeg = {
  miles: number;
  durationHours: number;
};

export type SimSegmentType = 'DRIVE' | 'BREAK_30M' | 'REST_10H' | 'RESTART_34H' | 'WAIT' | 'DWELL';

export type SimSegment = {
  type: SimSegmentType;
  from: string;
  to: string;
  hours: number;
  miles: number;
  startFraction: number;
  endFraction: number;
  comment: string;
};

export type SimDay = {
  date: string;
  driveHours: number;
  onDutyHours: number;
  restHours: number;
  miles: number;
};

export type SimStopResult = {
  stopIndex: number;
  label: string;
  arrival: string;
  departure: string;
  canMakeAppointment: boolean | null;
  slackHours: number | null;
  clocksAfter: SimClocks;
};

export type RouteBuddySimulateResponse = {
  segments: SimSegment[];
  days: SimDay[];
  stopResults: SimStopResult[];
  totalMiles: number;
  totalDriveHours: number;
  usedCycleRestart: boolean;
  clocksAtStart: SimClocks;
  verificationPassed: boolean;
  warnings: string[];
  timezone: string;
};

export type LatLngLiteral = {
  lat: number;
  lng: number;
};

export type StopLocation = {
  address: string;
  lat: number | null;
  lng: number | null;
};

export type StopLabel = 'PICKUP' | 'DELIVERY';

export type TripStop = {
  key: number;
  label: StopLabel;
  location: StopLocation;
  appointmentAt: string | null;
  dwellHours: number;
  departAt: string | null;
  // present on stops restored from a saved trip file, for planned-vs-actual comparison
  plannedArrival?: string | null;
  plannedDeparture?: string | null;
  done?: boolean;
};

// the downloadable trip file
export type SavedTrip = {
  version: 1;
  savedAt: string;
  eldDriverId: number;
  driverName: string;
  origin: StopLocation;
  tripDepartAt: string | null;
  plannedDepartAt: string;
  stops: {
    label: StopLabel;
    address: string;
    lat: number;
    lng: number;
    appointmentAt: string | null;
    dwellHours: number;
    departAt: string | null;
    plannedArrival: string;
    plannedDeparture: string;
    // present when the trip was loaded from the backend mid-trip; keeps the
    // completed stops ticked in the editor
    done?: boolean;
  }[];
};

// a stop of a persisted trip, as the backend stores it
export type TripStopRecord = {
  label: StopLabel;
  address: string;
  lat: number;
  lng: number;
  appointmentAt: string | null;
  dwellHours: number;
  departAt: string | null;
  plannedArrival: string;
  plannedDeparture: string;
  actualArrival: string | null;
  actualDeparture: string | null;
  done: boolean;
};

export type TripStopPrediction = {
  stopIndex: number;
  predictedArrival: string;
  // appointment minus predicted arrival; negative = late
  slackHours: number | null;
};

// a persisted trip (backend models_eld.Trip)
export type Trip = {
  id: number;
  createdAt: string;
  organizationId: number;
  userId: number;
  eldDriverId: number;
  eldVehicleId: number;
  eldVehicleUnitNumber: string;
  name: string;
  status: number;
  plannedDepartAt: string;
  originAddress: string;
  originLat: number;
  originLng: number;
  stops: TripStopRecord[];
  legs: SimLeg[];
  routePolyline: string;
  plannedResult: string;
  progressPercent: number;
  lateHours: number;
  offRouteMiles?: number;
  nextStopIndex: number;
  predictedArrivals: TripStopPrediction[];
  lastLat: number | null;
  lastLng: number | null;
  lastLocationAt: string | null;
  lastLocationSource: string;
  etaNextStopAt: string | null;
  lastTrackedAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  abandonedAt: string | null;
};

// one dispatch-board row (backend TrackBoardRow)
export type TrackRow = {
  eldDriverId: number;
  firstName: string;
  lastName: string;
  eldVehicleId: number;
  eldVehicleUnitNumber: string;
  connected: boolean;
  dutyStatus: string;
  moving: boolean;
  lat: number | null;
  lng: number | null;
  locationSource: string;
  locationAt: string | null;
  currentTrip: Trip | null;
  nextTrip: Trip | null;
  shippingDocs: string[] | null;
  inMyFleet: boolean;
};

// a dispatcher's truck assignment + alert preferences (backend models_eld.DispatcherFleet)
export type DispatcherFleet = {
  id: number;
  createdAt: string;
  organizationId: number;
  userId: number;
  eldVehicleIds: number[];
  notifyLate: boolean;
  lateThresholdHours: number;
  notifyOffRoute: boolean;
  notifyTripStarted: boolean;
  notifyTripFinished: boolean;
  notifyDisconnected: boolean;
  notifyMissingDocs: boolean;
};

// one row of GET /eld/dispatcherFleets
export type DispatcherFleetUser = {
  userId: number;
  name: string;
  email: string;
  isDispatcher: boolean;
  fleet: DispatcherFleet | null;
};

// what the dispatcher is currently placing with a map click
export type PickTarget = 'origin' | number | null;

export const EMPTY_STOP_LOCATION: StopLocation = { address: '', lat: null, lng: null };

let stopKeyCounter = 1;

export function newTripStop(label: StopLabel): TripStop {
  stopKeyCounter += 1;
  return {
    key: stopKeyCounter,
    label,
    location: EMPTY_STOP_LOCATION,
    appointmentAt: null,
    dwellHours: 1,
    departAt: null,
  };
}

export const STOP_LABELS: Record<StopLabel, string> = {
  PICKUP: 'Pickup',
  DELIVERY: 'Delivery',
};
